import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../api/client';
import { AuthContext } from '../context/AuthContext';

const inr = (v) => `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const date = (v) => (v ? new Date(v).toLocaleDateString('en-IN') : '-');
const STAFF = { rm: [['/rm/leads', 'Lead pipeline']], admin: [['/admin/spvs', 'Manage SPVs'], ['/admin/payments', 'Payments'], ['/admin/resale', 'Resale and referrals'], ['/admin/kyc', 'KYC review']],
  superadmin: [['/admin/spvs', 'Manage SPVs'], ['/admin/payments', 'Payments'], ['/admin/resale', 'Resale and referrals'], ['/admin/kyc', 'KYC review']], compliance: [['/admin/kyc', 'KYC review']] };

const Card = ({ title, children }) => (
  <section style={{ border: '1px solid #ddd', borderRadius: 8, padding: 16, margin: '16px 0' }}>
    <h2 style={{ marginTop: 0, fontSize: '1.15rem' }}>{title}</h2>
    {children}
  </section>
);

const Dashboard = () => {
  const { access, logout } = useContext(AuthContext);
  const [me, setMe] = useState(null);
  const [d, setD] = useState(null);

  useEffect(() => {
    Promise.all([api.get('/api/users/me/'), api.get('/api/dashboard/my/')])
      .then(([m, dash]) => { setMe(m.data); setD(dash.data); })
      .catch(() => toast.error('Failed to load your dashboard'));
  }, [access]);

  if (!d || !me) return <main style={{ padding: '4rem 1rem', textAlign: 'center' }}><p>Loading…</p></main>;

  return (
    <main style={{ maxWidth: 1000, margin: '0 auto', padding: '2rem 1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Welcome, {me.full_name || me.username}</h1>
        <button onClick={logout}>Logout</button>
      </div>

      {d.kyc.status !== 'approved' && (
        <Card title="Complete your KYC">
          <p>Status: {d.kyc.status.replace('_', ' ')}{d.kyc.reject_reason && ` — ${d.kyc.reject_reason}`}. You need approved KYC to view deals and invest.</p>
          <Link to="/kyc">Go to KYC</Link>
        </Card>
      )}

      {d.documents_to_sign.length > 0 && (
        <Card title="Documents waiting for your signature">
          <p>{d.documents_to_sign.length} document(s). <Link to="/my-documents">Review and sign</Link></p>
        </Card>
      )}

      <Card title="Summary">
        <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, margin: 0 }}>
          <div><dt>Companies held</dt><dd style={{ margin: 0, fontSize: '1.4rem' }}>{d.summary.spvs_held}</dd></div>
          <div><dt>Invested (allotted)</dt><dd style={{ margin: 0, fontSize: '1.4rem' }}>{inr(d.summary.total_invested)}</dd></div>
          <div><dt>Commitments in progress</dt><dd style={{ margin: 0, fontSize: '1.4rem' }}>{d.summary.commitments_in_progress}</dd></div>
        </dl>
      </Card>

      <Card title="Holdings">
        {d.holdings.length === 0 ? <p>No shares allotted yet. <Link to="/deals">Browse deals</Link></p> : (
          <table style={{ width: '100%' }}>
            <thead><tr><th align="left">Company</th><th align="left">Stage</th><th align="right">Shares</th><th align="right">% of allotted</th><th align="right">Invested</th></tr></thead>
            <tbody>{d.holdings.map((h) => (
              <tr key={h.spv}><td><Link to={`/deals/${h.spv}`}>{h.spv_name}</Link></td><td>{h.spv_status}</td><td align="right">{h.shares}</td>
                <td align="right">{Number(h.holding_pct).toFixed(2)}%</td><td align="right">{inr(h.invested)}</td></tr>
            ))}</tbody>
          </table>
        )}
        <p><Link to="/my-documents">Certificates and signed documents</Link>{d.holdings.map((h) => <span key={h.spv}> · <Link to={`/spvs/${h.spv}/expenses`}>Expenses ({h.spv_name})</Link></span>)}</p>
      </Card>

      {d.commitments_in_progress.length > 0 && (
        <Card title="Commitments in progress">
          <ul>{d.commitments_in_progress.map((c) => <li key={c.id}>{c.spv_name}: {c.shares} shares ({inr(c.amount)}) — {c.status.replace('_', ' ')}</li>)}</ul>
          <Link to="/commitments">Manage commitments</Link>
        </Card>
      )}

      {d.distributions.length > 0 && (
        <Card title="Distributions">
          <table style={{ width: '100%' }}>
            <thead><tr><th align="left">Company</th><th align="left">Payout date</th><th align="right">Gross</th><th align="right">TDS</th><th align="right">Net</th><th align="left">Status</th></tr></thead>
            <tbody>{d.distributions.map((x) => (
              <tr key={x.distribution}><td>{x.spv_name}</td><td>{date(x.payout_date)}</td><td align="right">{inr(x.gross)}</td><td align="right">{inr(x.tds)}</td><td align="right">{inr(x.net)}</td>
                <td>{x.utr ? `Paid (UTR ${x.utr})` : 'Approved, payout pending'}</td></tr>
            ))}</tbody>
          </table>
          {[...new Set(d.distributions.map((x) => x.spv))].map((sid) => <p key={sid}><Link to={`/spvs/${sid}/expenses`}>Expense report</Link></p>)}
        </Card>
      )}

      <Card title="Refer an investor">
        <p>Share your link. When someone you referred is allotted shares, a referral fee is recorded for you. It is paid by the company and never reduces anyone's returns.</p>
        <p><code>{d.referrals.link}</code> (code {d.referrals.code})</p>
        {d.referrals.referrals.length > 0 && (
          <ul>{d.referrals.referrals.map((r) => <li key={r.id}>{r.referred} · {r.spv_name} · {inr(r.amount)} · {r.status}{r.paid_utr && ` (UTR ${r.paid_utr})`}</li>)}</ul>
        )}
      </Card>

      <Card title="Milestones">
        {d.milestones.length === 0 ? <p>No milestones for your deals yet.</p> : (
          <table style={{ width: '100%' }}>
            <thead><tr><th align="left">Company</th><th align="left">Milestone</th><th align="left">Target</th><th align="left">Actual</th><th align="left">Status</th></tr></thead>
            <tbody>{d.milestones.map((m, i) => (
              <tr key={i}><td>{m.spv_name}</td><td>{m.type.replace('_', ' ')}{m.note && ` — ${m.note}`}</td><td>{date(m.target_date)}</td><td>{date(m.actual_date)}</td><td>{m.status}</td></tr>
            ))}</tbody>
          </table>
        )}
      </Card>

      <Card title="Updates">
        {d.updates.length === 0 ? <p>No updates yet.</p> : d.updates.map((u) => (
          <article key={u.id}><h3 style={{ marginBottom: 0 }}>{u.title}</h3><small>{u.spv_name} · {date(u.created_at)}</small><p>{u.body}</p></article>
        ))}
      </Card>

      <Card title="Recent payments">
        {d.payments.length === 0 ? <p>No payments yet.</p> : (
          <ul>{d.payments.map((p) => (
            <li key={p.id}>{date(p.created_at)} · {p.spv_name} · {inr(p.amount)} · {p.status}{p.refund_utr && ` (refund UTR ${p.refund_utr})`}</li>
          ))}</ul>
        )}
        <Link to="/payments">All payments</Link>
      </Card>

      <Card title="Account activity">
        {d.audit.length === 0 ? <p>No activity yet.</p> : (
          <ul>{d.audit.map((a) => <li key={a.id}>{new Date(a.created_at).toLocaleString('en-IN')} · {a.entity_type} · {a.action.replace(/_/g, ' ')}</li>)}</ul>
        )}
        <Link to="/audit">Full activity log</Link>
      </Card>

      <nav aria-label="Shortcuts">
        <Link to="/deals">Deals</Link> · <Link to="/resale">Resale</Link> · <Link to="/kyc">KYC</Link>
        {(STAFF[me.role] || []).map(([to, label]) => <span key={to}> · <Link to={to}>{label}</Link></span>)}
      </nav>
    </main>
  );
};

export default Dashboard;
